
import { useState } from 'react';

const commercialServices = [
  {
    icon: 'ri-store-2-line',
    title: 'Retail Store Buildouts',
    description:
      'Eye-catching retail environments designed to maximize foot traffic, enhance product displays, and create memorable shopping experiences that keep customers coming back.',
    features: [
      'Custom Fixtures & Displays',
      'Lighting Design',
      'ADA Compliance',
      'Brand Integration',
    ],
    image:
      'https://public.readdy.ai/ai/img_res/c28c7cf4dc17e6ea90ae759e750e4483.jpg',
  },
  {
    icon: 'ri-restaurant-2-line',
    title: 'Restaurant Renovations',
    description:
      'Full-service restaurant design and renovation from kitchen buildouts to dining room ambiance. We create spaces that delight guests and streamline operations.',
    features: [
      'Commercial Kitchen Design',
      'Dining Area Layout',
      'Bar & Lounge Build',
      'Health Code Compliance',
    ],
    image:
      'https://readdy.ai/api/search-image?query=stylish%20modern%20restaurant%20interior%20with%20warm%20ambient%20lighting%2C%20exposed%20brick%20walls%2C%20wooden%20tables%2C%20comfortable%20seating%2C%20open%20kitchen%20concept%2C%20contemporary%20dining%20atmosphere%2C%20professional%20commercial%20photography%2C%20warm%20earth%20tones&width=600&height=450&seq=commercial-restaurant-1&orientation=landscape',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Office & Commercial Spaces',
    description:
      'Productive, inspiring workspaces tailored to your business needs. From open-plan offices to executive suites, we build environments where teams thrive.',
    features: [
      'Space Planning',
      'Conference Rooms',
      'Break Areas',
      'Tech Infrastructure',
    ],
    image:
      'https://readdy.ai/api/search-image?query=modern%20commercial%20office%20space%20with%20open%20floor%20plan%2C%20glass%20partitions%2C%20contemporary%20furniture%2C%20natural%20light%2C%20collaborative%20workspace%2C%20professional%20architectural%20photography%2C%20clean%20minimalist%20design%2C%20warm%20neutral%20palette&width=600&height=450&seq=commercial-office-1&orientation=landscape',
  },
];

const commercialProjects = [
  {
    title: 'Artisan Coffee House',
    type: 'Restaurant',
    location: 'Downtown LA',
    image:
      'https://readdy.ai/api/search-image?query=trendy%20artisan%20coffee%20shop%20interior%20with%20exposed%20brick%2C%20industrial%20lighting%2C%20wooden%20counters%2C%20espresso%20bar%2C%20cozy%20seating%20areas%2C%20warm%20ambient%20atmosphere%2C%20professional%20commercial%20interior%20photography&width=400&height=500&seq=commercial-proj-coffee&orientation=portrait',
  },
  {
    title: 'Luxe Fashion Boutique',
    type: 'Retail',
    location: 'Beverly Hills, CA',
    image:
      'https://readdy.ai/api/search-image?query=luxury%20fashion%20boutique%20interior%20with%20marble%20floors%2C%20gold%20accents%2C%20elegant%20clothing%20displays%2C%20modern%20lighting%2C%20high-end%20retail%20design%2C%20sophisticated%20atmosphere%2C%20professional%20commercial%20photography&width=400&height=500&seq=commercial-proj-boutique&orientation=portrait',
  },
  {
    title: 'Farm-to-Table Bistro',
    type: 'Restaurant',
    location: 'Santa Monica, CA',
    image:
      'https://readdy.ai/api/search-image?query=charming%20farm%20to%20table%20restaurant%20interior%20with%20rustic%20wooden%20beams%2C%20greenery%2C%20natural%20materials%2C%20warm%20lighting%2C%20communal%20dining%20tables%2C%20fresh%20modern%20rustic%20design%2C%20professional%20interior%20photography&width=400&height=500&seq=commercial-proj-bistro&orientation=portrait',
  },
  {
    title: 'Tech Startup HQ',
    type: 'Office',
    location: 'Venice, CA',
    image:
      'https://readdy.ai/api/search-image?query=modern%20tech%20startup%20office%20with%20open%20workspace%2C%20colorful%20accents%2C%20standing%20desks%2C%20lounge%20area%2C%20large%20windows%2C%20creative%20collaborative%20environment%2C%20professional%20architectural%20photography%2C%20bright%20and%20airy&width=400&height=500&seq=commercial-proj-tech&orientation=portrait',
  },
];

export default function Commercial() {
  const [activeTab, setActiveTab] = useState(0);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="commercial" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-sm font-semibold text-gray-600 tracking-wider uppercase mb-3">
            COMMERCIAL SERVICES
          </p>
          <h2
            className="text-4xl md:text-5xl font-light text-gray-900 mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Retail, Restaurant &amp; Commercial Remodeling
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            We bring the same award-winning craftsmanship to commercial spaces — creating
            environments that elevate your brand and drive business growth.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {[
            { value: '150+', label: 'Commercial Projects' },
            { value: '98%', label: 'On-Time Delivery' },
            { value: '45', label: 'Restaurant Buildouts' },
            { value: '60+', label: 'Retail Stores' },
          ].map((stat, i) => (
            <div key={i} className="text-center py-6 px-4 bg-stone-50 rounded-2xl">
              <p
                className="text-3xl md:text-4xl font-bold text-gray-900 mb-1"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {stat.value}
              </p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabbed Service Cards */}
        <div className="mb-20">
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-stone-100 rounded-full px-1 py-1">
              {commercialServices.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTab(i)}
                  className={`px-6 py-2.5 text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    activeTab === i
                      ? 'bg-gray-900 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {s.title}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="relative h-[420px] w-full rounded-3xl overflow-hidden shadow-xl">
              <img
                src={commercialServices[activeTab].image}
                alt={commercialServices[activeTab].title}
                className="w-full h-full object-cover object-top transition-opacity duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i
                      className={`${commercialServices[activeTab].icon} text-lg text-gray-900`}
                    ></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {commercialServices[activeTab].title}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 mb-6">
                <i
                  className={`${commercialServices[activeTab].icon} text-2xl text-white`}
                ></i>
              </div>
              <h3
                className="text-3xl font-semibold text-gray-900 mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {commercialServices[activeTab].title}
              </h3>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                {commercialServices[activeTab].description}
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {commercialServices[activeTab].features.map((feature, fi) => (
                  <div key={fi} className="flex items-center gap-3">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-amber-100">
                      <i className="ri-check-line text-sm text-amber-600"></i>
                    </div>
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={scrollToContact}
                className="px-8 py-3 bg-gray-900 text-white text-sm font-semibold rounded-full hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer"
              >
                Request Commercial Quote
              </button>
            </div>
          </div>
        </div>

        {/* Recent Commercial Projects */}
        <div>
          <div className="flex items-center justify-between mb-10">
            <div>
              <h3
                className="text-2xl font-semibold text-gray-900"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Recent Commercial Projects
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Spaces we&apos;ve transformed for thriving businesses
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {commercialProjects.map((project, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="relative h-72 w-full rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity"></div>
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-semibold text-gray-900">
                      {project.type}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h4 className="text-lg font-semibold text-white mb-1">
                      {project.title}
                    </h4>
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-line text-sm text-white/80"></i>
                      </div>
                      <span className="text-xs text-white/80">{project.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 relative rounded-3xl overflow-hidden">
          <div className="absolute inset-0 w-full h-full">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20commercial%20building%20exterior%20at%20dusk%20with%20warm%20interior%20lighting%20visible%20through%20large%20glass%20windows%2C%20contemporary%20architecture%2C%20professional%20real%20estate%20photography%2C%20warm%20amber%20tones%2C%20elegant%20urban%20setting&width=1200&height=400&seq=commercial-cta-bg&orientation=landscape"
              alt="Commercial remodeling"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
          </div>
          <div className="relative z-10 py-16 px-8 md:px-16 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3
                className="text-3xl font-light text-white mb-3"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Ready to Transform Your Business Space?
              </h3>
              <p className="text-base text-white/80 max-w-lg">
                From concept to completion, we handle every detail of your commercial remodel.
                Get a free consultation today.
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={scrollToContact}
                className="px-8 py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-full hover:from-amber-600 hover:to-orange-600 transition-all shadow-xl whitespace-nowrap cursor-pointer"
              >
                Get Free Estimate
              </button>
              <a
                href="tel:5551234567"
                className="px-8 py-3.5 bg-white/10 backdrop-blur-sm text-white text-sm font-semibold rounded-full hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-sm"></i>
                </div>
                Call Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
